import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CircleIcon from "@mui/icons-material/Circle";

import { NameValue, OverviewChart } from "../../../Models/Models";
import {
  GetBinCardData,
  GetDLCCardAllCategoriesData,
  GetDLCCardData,
  GetTotalBinCardData,
} from "../../../ApiServices/ApiRequests";
import {
  useWasteManagement,
  WasteManagementType,
} from "../../../ContextAPI/WasteManagementContext";
import { useTranslation } from "react-i18next";
import getDLCIconDataUrl from "../../../Assests/SVG/DLCIcon";
import getBinIconDataUrl from "../../../Assests/SVG/BinIcon";
import DlcLow from "../../../Assests/SVG/DlcLow.svg";
import DlcMedium from "../../../Assests/SVG/DlcMedium.svg";
import DlcHigh from "../../../Assests/SVG/DlcHigh.svg";
import { useMasterData } from "../../../ContextAPI/MasterContext";
import Loading from "../../../Controller/LoadingController/Loading";

const useStyles = makeStyles(() => ({
  card: {
    textAlign: "center",
    borderRadius: "12px",
    backgroundColor: "#fff",
    height: "480px",
    marginTop: "20px",
  },
  totalBins: {
    fontSize: "50px", // Large number size
    fontWeight: "bold",
    color: "#8850ef", // Purple color
  },
  wasteType: {
    display: "flex",
    justifyContent: "space-around",
  },
  wasteLabel: {
    textAlign: "center",
    color: "#757575",
    fontSize: "12px",
  },
  categoryFilter: {
    marginTop: "15px",
    padding: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#8850ef",
    width: "85%",
    marginLeft: 30,
  },
  listItem: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "2px",
  },
  listIcon: {
    fontSize: "12px", // Adjust icon size
  },
  percentage: {
    marginLeft: "8px",
    color: "#757575",
    fontSize: "12px",
  },
  wasteValue: {
    color: "#8850ef", // Same purple color for waste values
    fontSize: "20px", // Value size
    fontWeight: "bold",
  },

  scrollableSection: {
    maxHeight: "95px", // Set max-height
    overflowY: "auto", // Enable vertical scrolling
    "&::-webkit-scrollbar": {
      width: "3px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#ffffff",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#8850EF",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#c4c4c4",
    },
  },
}));

const DLCCard: React.FC = () => {
  const classes = useStyles();
  const { masterState } = useMasterData();

  const [loading, setLoading] = useState(true);

  const { wasteManagementState, dispatchWasteManagement } =
    useWasteManagement();
  const { t } = useTranslation();

  const handleInputChange = useCallback(
    (
      field: keyof WasteManagementType,
      value: string | boolean | number | null | NameValue[] | OverviewChart
    ) => {
      dispatchWasteManagement({ type: "SET_FIELD", field, value });
    },
    [dispatchWasteManagement]
  );

  const handleBulkChange = useCallback(
    (value: Partial<WasteManagementType>) => {
      dispatchWasteManagement({ type: "BULK_UPDATE", value });
    },
    [dispatchWasteManagement]
  );

  // Fetch bin card data when the component mounts
  useEffect(() => {
    const fetchDLCCardData = async (
      siteCode: string,
      date: string
    ): Promise<void> => {
      try {
        setLoading(true);
        const data: NameValue[] = await GetDLCCardAllCategoriesData(
          siteCode,
          date
        );
        handleInputChange("DLCAvgData", data);
      } catch (err) {
        console.error("Error fetching bin card data:", err);
      } finally {
        setLoading(false);
      }
    };
    if (masterState.selectedSiteCode.length > 0 && masterState.selectedDate) {
      fetchDLCCardData(
        masterState.selectedSiteCode,
        masterState.selectedDate.format("YYYY-MM-DD")
      );
    }
  }, [masterState.selectedSiteCode, masterState.selectedDate]);

  // Fetch total bin card data
  useEffect(() => {
    const fetchTotalBinCardData = async (
      siteCode: string,
      date: string
    ): Promise<void> => {
      try {
        setLoading(true);
        const data: NameValue[] = await GetDLCCardData(siteCode, date);
        handleInputChange("DLCStsType", data);
      } catch (err) {
        console.error("Error fetching total bin card data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (masterState.selectedSiteCode.length > 0 && masterState.selectedDate) {
      fetchTotalBinCardData(
        masterState.selectedSiteCode,
        masterState.selectedDate.format("YYYY-MM-DD")
      );
    }
  }, [masterState.selectedSiteCode, masterState.selectedDate]);

  // if (loading) return <CircularProgress />;

  // if (loading) {
  //   return (
  //     <Card className={classes.card}>
  //       <Box
  //         display="flex"
  //         alignItems="center"
  //         justifyContent="center"
  //         height="100%"
  //         minHeight="200px" // Adjust as needed for visual centering
  //       >
  //         <CircularProgress />
  //       </Box>
  //     </Card>
  //   );
  // }

  return (
    <>
      {loading ? (
        <Loading loading={loading} type={`dots`} backDrop={false} />
      ) : (
        <Card
          className={classes.card}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Total Bins Monitoring */}
          <Typography variant="h6" color="textSecondary" mt={5}>
            {t("global.TotalDICMonitoring")}
          </Typography>

          <Typography className={classes.totalBins} variant="h3">
            {wasteManagementState.DLCStsType?.xaxes
              ? wasteManagementState.DLCStsType?.xaxes
              : "0"}
          </Typography>

          <Box className={classes.wasteType}>
            {wasteManagementState.DLCStsType?.yaxes?.map((waste, index) => {
              let imageSrc;
              if (waste.name === "Critical") {
                imageSrc = DlcHigh;
              } else if (waste.name === "Moderate") {
                imageSrc = DlcMedium;
              } else if (waste.name === "Clean") {
                imageSrc = DlcLow;
              }
              return (
                <Box key={index} style={{ margin: "5px" }}>
                  <img
                    src={typeof imageSrc === "string" ? imageSrc : undefined}
                    alt={waste.name}
                    style={{ width: "50px", height: "50px" }}
                  />
                  <Typography className={classes.wasteLabel}>
                    {waste.name}
                  </Typography>
                  <Typography className={classes.wasteValue} variant="h5">
                    {waste.value}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Divider sx={{ marginTop: "20px" }} />

          {/* Category Filter */}
          <Box className={classes.categoryFilter}>
            <CircleIcon sx={{ fontSize: 12, marginRight: "8px" }} />
            <Typography variant="subtitle1" style={{ fontSize: "14px" }}>
              {t("global.AllCategories")}
            </Typography>
          </Box>

          <List className={classes.scrollableSection}>
            {wasteManagementState.DLCAvgData &&
            wasteManagementState.DLCAvgData.length > 0 ? (
              wasteManagementState.DLCAvgData?.map((bin, index) => (
                <div key={index} className={classes.listItem}>
                  <div style={{ display: "flex" }}>
                    <CircleOutlinedIcon
                      sx={{
                        color:
                          bin.name === "Clean"
                            ? "#4caf50"
                            : bin.name === "Moderate"
                            ? "#ff9800"
                            : bin.name === "Critical"
                            ? "#f44336"
                            : "#9e9e9e", // Default color if none matches
                        fontSize: "20px",
                        marginLeft: "25px",
                      }}
                    />

                    <Typography
                      style={{
                        fontSize: "14px",
                        wordWrap: "break-word",
                        marginLeft: "5px",
                      }}
                    >
                      {bin.name}
                    </Typography>
                  </div>
                  <Typography
                    className={classes.percentage}
                    style={{
                      fontSize: "15px",
                      marginRight: "25px",
                      wordWrap: "break-word",
                    }}
                  >
                    {bin.value ? bin.value : "0"}%
                  </Typography>
                </div>
              ))
            ) : (
              <Typography
                variant="h6"
                color="textSecondary"
                align="center"
                style={{ padding: "0px" }}
              >
                {t("global.NoDataFound")}
              </Typography>
            )}
          </List>

          {/* This will be pushed to the bottom */}
          <div style={{ marginTop: "auto" }}>
            <Typography variant="caption" color="textSecondary">
              {t("global.LevelIn%")}
            </Typography>
          </div>
        </Card>
      )}
    </>
  );
};

export default DLCCard;
